/**
 * EID-126: pure transform-status state machine.
 *
 * A transform's lifecycle used to be observable only through the console — a
 * failed init resolved null, a failed module import / query / validation /
 * timeout logged and vanished. This module defines the status contract the
 * Transform class reports through its `onStatus` callback and the
 * data-provider publishes per transform id (alongside the data registry) so
 * UIs can subscribe.
 *
 * States: `initializing` (worker starting or code recompiling),
 * `importing-modules` (CDN module imports in flight), `ready` (compiled and
 * queryable), `error` (with the failing phase, message, stack, and — for
 * phases raised from compiled user code — the user-code line offset within
 * the generated ES module, so editors can map worker stack frames to editor
 * markers).
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note (transform-input-signature.ts
 * precedent).
 */

export type TransformErrorPhase =
  'init' | 'import' | 'query' | 'validation' | 'timeout';

export interface TransformErrorInfo {
  phase: TransformErrorPhase;
  message: string;
  stack?: string;
  /**
   * Number of generated lines (imports + the `export default` wrapper line)
   * preceding the first user-code line in the compiled transform module.
   * Editor line = worker stack-frame line − codeLineOffset.
   */
  codeLineOffset?: number;
}

export type TransformStatus =
  | { state: 'initializing' }
  | { state: 'importing-modules' }
  | { state: 'ready' }
  | ({ state: 'error' } & TransformErrorInfo);

export type TransformStatusEvent =
  | { type: 'initialize' }
  | { type: 'import-modules' }
  | { type: 'ready' }
  | ({ type: 'error' } & TransformErrorInfo);

export const initializingStatus = (): TransformStatus => ({
  state: 'initializing',
});

export const errorStatus = (
  phase: TransformErrorPhase,
  message: string,
  extra?: { stack?: string; codeLineOffset?: number },
): TransformStatus => ({
  state: 'error',
  phase,
  message,
  ...(extra?.stack !== undefined ? { stack: extra.stack } : {}),
  ...(extra?.codeLineOffset !== undefined
    ? { codeLineOffset: extra.codeLineOffset }
    : {}),
});

/**
 * Whether an error phase is recoverable by a subsequent successful query.
 * Query/validation/timeout errors are per-query — the compiled function is
 * still live, so the next successful query returns the transform to `ready`.
 * init/import errors mean there is no (new) compiled function; only a
 * successful re-initialize/recompile clears them, and a stray `ready` event
 * (e.g. a query served by the previous still-loaded code after a failed warm
 * recompile) must not mask them.
 */
export const isRecoverableErrorPhase = (phase: TransformErrorPhase): boolean =>
  phase === 'query' || phase === 'validation' || phase === 'timeout';

/**
 * Reduce the current status by an event. Illegal transitions return the
 * `current` object identity unchanged, so callers can dedupe emissions on
 * reference equality.
 *
 * - `initialize` — legal from anywhere (worker start, warm recompile).
 * - `import-modules` — only from `initializing`.
 * - `ready` — from `initializing` / `importing-modules`, idempotent from
 *   `ready`, and from `error` only when the phase is recoverable (see
 *   isRecoverableErrorPhase).
 * - `error` — legal from anywhere.
 */
export const nextTransformStatus = (
  current: TransformStatus,
  event: TransformStatusEvent,
): TransformStatus => {
  switch (event.type) {
    case 'initialize':
      return { state: 'initializing' };
    case 'import-modules':
      return current.state === 'initializing'
        ? { state: 'importing-modules' }
        : current;
    case 'ready':
      if (current.state === 'ready') return current;
      if (current.state === 'error' && !isRecoverableErrorPhase(current.phase))
        return current;
      return { state: 'ready' };
    case 'error':
      return errorStatus(event.phase, event.message, {
        stack: event.stack,
        codeLineOffset: event.codeLineOffset,
      });
  }
};
